import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import './AdminPage.css';
import UnauthorizedPage from '../exceptions/UnauthorizedPage';

function AdminPage() {
    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        startTime: '',
        endTime: '',
        cost: '',
        costKsv: '',
        costKulaline: '',
        costVil: '',
        costEiorliige: '',
        participantLimit: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [users, setUsers] = useState([]);
    const [editingUserId, setMuudaingUserId] = useState(null);
    const [editUserData, setMuudaUserData] = useState({
        username: '',
        balance: '',
        role: ''
    });
    const navigate = useNavigate();

    const decodeJWT = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error decoding JWT:', error);
            return null;
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            const decoded = decodeJWT(token);
            if (decoded && decoded.role) {
                const userRole = String(decoded.role).toUpperCase();
                if (userRole === 'ADMIN') {
                    setIsAuthorized(true);
                    fetchEvents();
                    fetchUsers();
                }
            }
        }
        setIsCheckingAuth(false);
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axiosInstance.get('/api/private/users');
            setUsers(response.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            setMessage('Kasutajate laadimine ebaõnnestus');
        }
    };

    const fetchEvents = async () => {
        try {
            const response = await axiosInstance.get('/api/events');
            setEvents(response.data || []);
        } catch (error) {
            console.error('Viga ürituste laadimisel:', error);
            setMessage('Ürituste laadimine ebaõnnestus');
        }
    };

    const handleEventSelect = async (eventId) => {
        setSelectedEventId(eventId);
        if (!eventId) {
            setSelectedEvent(null);
            setFormData({
                name: '',
                location: '',
                startTime: '',
                endTime: '',
                cost: '',
                costKsv: '',
                costKulaline: '',
                costVil: '',
                participantLimit: ''
            });
            return;
        }

        try {
            const response = await axiosInstance.get(`/api/events/${eventId}`);
            const event = response.data;
            setSelectedEvent(event);
            setFormData({
                name: event.name || '',
                location: event.location || '',
                startTime: event.startTime || '',
                endTime: event.endTime || '',
                cost: event.cost || '',
                costKsv: event.costKsv || '',
                costKulaline: event.costKulaline || '',
                costVil: event.costVil || '',
                costEiorliige: event.costEiorliige || '',
                participantLimit: event.participantLimit || ''
            });
        } catch (error) {
            console.error('Error fetching event:', error);
            setMessage('Ürituse andmete laadimine ebaõnnestus');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedEventId) {
            setMessage('Please select an event to edit');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const updateData = {
                ...formData,
                cost: formData.cost ? parseFloat(formData.cost) : null,
                costKsv: formData.costKsv ? parseFloat(formData.costKsv) : null,
                costKulaline: formData.costKulaline ? parseFloat(formData.costKulaline) : null,
                costVil: formData.costVil ? parseFloat(formData.costVil) : null,
                costEiorliige: formData.costEiorliige ? parseFloat(formData.costEiorliige) : null,
                participantLimit: formData.participantLimit ? parseInt(formData.participantLimit) : null
            };

            await axiosInstance.put(`/api/events/${selectedEventId}`, updateData);
            setMessage('Üritus on edukalt uuendatud!');
            fetchEvents();
            
            // Reset form after a short delay
            setTimeout(() => {
                setSelectedEventId('');
                setSelectedEvent(null);
                setFormData({
                    name: '',
                    location: '',
                    startTime: '',
                    endTime: '',
                    cost: '',
                    costKsv: '',
                    costKulaline: '',
                    costVil: '',
                    participantLimit: ''
                });
                setMessage('');
            }, 2000);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Ürituse uuendamine ebaõnnestus';
            setMessage(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveGuest = async (guestId) => {
        if (!guestId) return;
        if (!window.confirm('Remove this guest from the event?')) return;
        setLoading(true);
        setMessage('');
        try {
            const response = await axiosInstance.delete(`/api/events/guest/${guestId}`);
            setSelectedEvent(response.data);
            setMessage('Guest removed.');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Failed to remove guest');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveUser = async (userId) => {
        if (!selectedEventId) {
            setMessage('Viga: Ei event selected');
            return;
        }

        if (!userId) {
            setMessage('Viga: User ID is missing. Please refresh the page and try again.');
            console.error('User ID is missing. User object:', selectedEvent?.users?.find(u => !u.id));
            return;
        }

        if (!window.confirm('Are you sure you want to remove this user from the event? The first person on the waitlist will be automatically added.')) {
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            await axiosInstance.post(`/api/events/leaveEvent/${userId}/${selectedEventId}`);
            setMessage('Kasutaja eemaldati. Esimene ootenimekirjas olnud kasutaja lisati osalejaks.');

            const response = await axiosInstance.get(`/api/events/${selectedEventId}`);
            setSelectedEvent(response.data);
            
            setTimeout(() => {
                setMessage('');
            }, 3000);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Kasutaja eemaldamine ebaõnnestus';
            setMessage(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleMuudaUser = (user) => {
        setMuudaingUserId(user.id);
        setMuudaUserData({
            username: user.username || '',
            balance: user.balance || 0,
            role: user.role || 'USER'
        });
    };

    const handleLoobuMuuda = () => {
        setMuudaingUserId(null);
        setMuudaUserData({
            username: '',
            balance: '',
            role: ''
        });
    };

    const handleSalvestaUser = async (userId) => {
        setLoading(true);
        setMessage('');

        try {
            const updateData = {
                username: editUserData.username,
                balance: parseFloat(editUserData.balance),
                role: editUserData.role
            };

            await axiosInstance.put(`/api/private/users/${userId}`, updateData);
            setMessage('User updated successfully!');
            fetchUsers();
            
            setTimeout(() => {
                setMessage('');
            }, 2000);
            
            handleLoobuMuuda();
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Error updating user';
            setMessage(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleMuudaUserInputChange = (e) => {
        const { name, value } = e.target;
        setMuudaUserData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSetEmailVerified = async (userId, emailVerified) => {
        setLoading(true);
        setMessage('');

        try {
            await axiosInstance.put(`/api/private/users/${userId}/email-verified`, emailVerified, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setMessage('Email verified status updated successfully!');
            fetchUsers();
            
            setTimeout(() => {
                setMessage('');
            }, 2000);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Meiliaadressi kinnitamise oleku muutmine ebaõnnestus';
            setMessage(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (isCheckingAuth) {
        return <div className="admin-page">Laadimine...</div>;
    }

    if (!isAuthorized) {
        return <UnauthorizedPage />;
    }

    return (
        <div className="admin-page">
            <div className="admin-container">
                <h1 className="admin-title">Administraatori töölaud</h1>
                
                <div className="admin-section">
                    <h2 className="admin-section-title">Muuda üritust</h2>
                    
                    <div className="form-group">
                        <label htmlFor="eventSelect">Vali üritus:</label>
                        <select
                            id="eventSelect"
                            value={selectedEventId}
                            onChange={(e) => handleEventSelect(e.target.value)}
                            className="form-select"
                        >
                            <option value="">-- Vali üritus --</option>
                            {events.map(event => (
                                <option key={event.id} value={event.id}>
                                    {event.name || `Üritus nr ${event.id}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedEvent && (
                        <form onSubmit={handleSubmit} className="edit-event-form">
                            <div className="form-group">
                                <label htmlFor="name">Ürituse nimi:</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="location">Asukoht:</label>
                                <input
                                    type="text"
                                    id="location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="startTime">Algusaeg (pp.kk.aaaa HH:mm):</label>
                                <input
                                    type="text"
                                    id="startTime"
                                    name="startTime"
                                    value={formData.startTime}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder="dd.MM.yyyy HH:mm"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="endTime">Lõpuaeg (pp.kk.aaaa HH:mm):</label>
                                <input
                                    type="text"
                                    id="endTime"
                                    name="endTime"
                                    value={formData.endTime}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder="dd.MM.yyyy HH:mm"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="costKsv">Osalustasu ksv!-le:</label>
                                <input
                                    type="number"
                                    id="costKsv"
                                    name="costKsv"
                                    value={formData.costKsv}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="costKulaline">Osalustasu külalisele:</label>
                                <input
                                    type="number"
                                    id="costKulaline"
                                    name="costKulaline"
                                    value={formData.costKulaline}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="costVil">Osalustasu vil!-le:</label>
                                <input
                                    type="number"
                                    id="costVil"
                                    name="costVil"
                                    value={formData.costVil}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="costEiorliige">Osalustasu noorliikmele:</label>
                                <input
                                    type="number"
                                    id="costEiorliige"
                                    name="costEiorliige"
                                    value={formData.costEiorliige}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    step="0.01"
                                    min="0"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="participantLimit">Osalejate piirarv:</label>
                                <input
                                    type="number"
                                    id="participantLimit"
                                    name="participantLimit"
                                    value={formData.participantLimit}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    min="1"
                                />
                            </div>

                            {message && (
                                <div className={`message ${message.includes('Viga') ? 'error' : 'success'}`}>
                                    {message}
                                </div>
                            )}

                            <button type="submit" className="submit-btn" disabled={loading}>
                                {loading ? 'Updating...' : 'Update Event'}
                            </button>
                        </form>
                    )}
                </div>

                {selectedEvent && (
                    <div className="admin-section">
                        <h2 className="admin-section-title">Halda osalejaid</h2>
                        
                        <div className="participants-section">
                            <h3>Participants ({(selectedEvent.users?.length || 0) + (selectedEvent.guests?.length || 0)}
                                {selectedEvent.participantLimit ? ` / ${selectedEvent.participantLimit}` : ''})</h3>
                            {Array.isArray(selectedEvent.users) && selectedEvent.users.length > 0 ? (
                                <div className="users-list">
                                    {selectedEvent.users.map((user, index) => (
                                        <div key={user.id || user.username || index} className="user-card">
                                            <div className="user-info">
                                                <div className="user-name">
                                                    {user.firstName && user.lastName 
                                                        ? `${user.firstName} ${user.lastName}`
                                                        : user.username}
                                                </div>
                                                {user.role && <div className="user-email">{user.role}</div>}
                                                {user.email && (
                                                    <div className="user-email">{user.email}</div>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleRemoveUser(user.id)}
                                                className="remove-btn"
                                                disabled={loading || !user.id}
                                                title={!user.id ? 'Kasutaja ID puudub - eemaldamine pole võimalik' : 'Eemalda kasutaja ürituselt'}
                                            >
                                                Eemalda
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {Array.isArray(selectedEvent.guests) && selectedEvent.guests.length > 0 ? (
                                <div className="users-list" style={{ marginTop: selectedEvent.users?.length ? '12px' : 0 }}>
                                    {selectedEvent.guests.map((g, index) => (
                                        <div key={'g-' + (g.id || index)} className="user-card">
                                            <div className="user-info">
                                                <div className="user-name">{g.name}</div>
                                                {g.role && <div className="user-email">{g.role}</div>}
                                            </div>
                                            <button
                                                onClick={() => handleRemoveGuest(g.id)}
                                                className="remove-btn"
                                                disabled={loading || !g.id}
                                                title="Remove guest from event"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {(!selectedEvent.users?.length && !selectedEvent.guests?.length) ? (
                                <div className="no-users">Ei participants</div>
                            ) : null}
                        </div>

                        {Array.isArray(selectedEvent.waitlist) && selectedEvent.waitlist.length > 0 && (
                            <div className="waitlist-section">
                                <h3>Ootenimekiri ({selectedEvent.waitlist.length})</h3>
                                <div className="users-list">
                                    {selectedEvent.waitlist.map((user, index) => (
                                        <div key={user.id || user.username || index} className="user-card">
                                            <div className="user-info">
                                                <div className="user-name">
                                                    {user.firstName && user.lastName 
                                                        ? `${user.firstName} ${user.lastName}`
                                                        : user.username}
                                                </div>
                                                {user.email && (
                                                    <div className="user-email">{user.email}</div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="admin-section">
                    <h2 className="admin-section-title">Halda kasutajaid</h2>
                    
                    {message && (
                        <div className={`message ${message.includes('Viga') ? 'error' : 'success'}`}>
                            {message}
                        </div>
                    )}

                    {users.length > 0 ? (
                        <div className="users-list">
                            {users.map((user) => (
                                <div key={user.id} className="user-card">
                                    {editingUserId === user.id ? (
                                        <div className="edit-user-form">
                                            <div className="form-group">
                                                <label>Kasutajanimi:</label>
                                                <input
                                                    type="text"
                                                    name="username"
                                                    value={editUserData.username}
                                                    onChange={handleMuudaUserInputChange}
                                                    className="form-input"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Saldo:</label>
                                                <input
                                                    type="number"
                                                    name="balance"
                                                    value={editUserData.balance}
                                                    onChange={handleMuudaUserInputChange}
                                                    className="form-input"
                                                    step="0.01"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Roll:</label>
                                                <select
                                                    name="role"
                                                    value={editUserData.role}
                                                    onChange={handleMuudaUserInputChange}
                                                    className="form-select"
                                                    required
                                                >
                                                    <option value="noorliige">noorliige</option>
                                                    <option value="ksv!">ksv!</option>
                                                    <option value="vil!">vil!</option>
                                                    <option value="külaline">külaline</option>
                                                </select>
                                            </div>
                                            <div className="edit-user-actions">
                                                <button
                                                    onClick={() => handleSalvestaUser(user.id)}
                                                    className="save-btn"
                                                    disabled={loading}
                                                >
                                                    Salvesta
                                                </button>
                                                <button
                                                    onClick={handleLoobuMuuda}
                                                    className="cancel-btn"
                                                    disabled={loading}
                                                >
                                                    Loobu
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="user-info">
                                                <div className="user-name">
                                                    {user.firstName && user.lastName 
                                                        ? `${user.firstName} ${user.lastName}`
                                                        : user.username}
                                                </div>
                                                <div className="user-details">
                                                    <div><strong>Kasutajanimi:</strong> {user.username}</div>
                                                    <div><strong>Saldo:</strong> {user.balance}</div>
                                                    <div><strong>Roll:</strong> {user.role}</div>
                                                    {user.email && (
                                                        <div><strong>Meil:</strong> {user.email}</div>
                                                    )}
                                                    <div><strong>Meil kinnitatud:</strong> {user.emailVerified ? 'Jah' : 'Ei'}</div>
                                                </div>
                                            </div>
                                            <div className="user-actions">
                                                <button
                                                    onClick={() => navigate(`/admin/users/${user.id}/transactions`)}
                                                    className="transactions-btn"
                                                >
                                                    Vaata kasutaja tehinguid
                                                </button>
                                                <button
                                                    onClick={() => handleMuudaUser(user)}
                                                    className="edit-btn"
                                                    disabled={loading}
                                                >
                                                    Muuda
                                                </button>
                                                {!user.emailVerified && (
                                                    <button
                                                        onClick={() => handleSetEmailVerified(user.id, true)}
                                                        className="verify-btn"
                                                        disabled={loading}
                                                    >
                                                        Kinnita meiliaadress
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-users">Kasutajaid ei leitud</div>
                    )}
                </div>

            </div>
        </div>
    );
}

export default AdminPage;
